/**
 * Cross-module X XWP-16 (02_DESIGN §3.2; brief §3.5, §3.6) — every French string of the /magasins
 * workspace: the tree, the detail grids, the « Emplacements » and « Transferts » views, and the
 * dialogs « Répartir », « Transférer », « Ajuster la répartition », « Historique de répartition »,
 * magasin and emplacement forms. Never a literal in a component (one place to review the copy).
 * The two invariant sentences repeat the server's verbatim (MESSAGES_PLACEMENT, parity-tested).
 */
import { fmtInt } from "./locale.config.js";

const pluriel = (n, un, plusieurs) => (Math.abs(Number(n)) > 1 ? plusieurs : un);
const unites = (n) => `${fmtInt(n)} ${pluriel(n, "unité", "unités")}`;
const signe = (n) => (Number(n) > 0 ? `+${fmtInt(n)}` : Number(n) < 0 ? `−${fmtInt(-Number(n))}` : "0");

export const MAGASINS = Object.freeze({
  titre: "Magasins",
  sousTitre: "Stock global, magasins, emplacements et lots — où se trouve chaque unité.",
  vuesLabel: "Vues des magasins",
  vues: Object.freeze({ ensemble: "Vue d'ensemble", emplacements: "Emplacements", transferts: "Transferts" }),
  actions: Object.freeze({
    nouveauMagasin: "Nouveau magasin",
    nouvelEmplacement: "Nouvel emplacement",
    nouveauTransfert: "Nouveau transfert",
    modifierMagasin: "Modifier le magasin",
    modifierEmplacement: "Modifier l'emplacement",
    supprimerEmplacement: "Supprimer l'emplacement",
    placerLot: "Placer un lot ici",
    rangerLot: "Ranger un lot ici",
    transfererDepuis: "Transférer depuis ce magasin",
    voirStock: "Voir dans Stock & lots",
    voirMagasin: "Voir le magasin",
    ouvrir: "Ouvrir",
    ouvrirProduit: "Ouvrir le produit",
    repartir: "Répartir",
    transferer: "Transférer",
    /** WP-100 (CP §11): why « Transférer » is unavailable on a lot of this node. */
    transfererIndisponible: "Aucune unité de ce lot dans ce magasin : rien à transférer.",
    ajuster: "Ajuster la répartition",
    historique: "Historique de répartition",
    corriger: "Corriger le stock",
    ouvrirArbre: "Ouvrir dans la vue d'ensemble",
    valider: "Valider le transfert",
    annuler: "Annuler le brouillon",
    imprimer: "Imprimer le bon de transfert",
    reessayer: "Réessayer",
    plus: "Plus d'actions",
  }),
  produitFiltre: (nom) => `Produit : ${nom ?? "…"}`,
  produitFiltreAide: "Les quantités de l'arborescence ne comptent que ce produit.",
  chargement: "Chargement…",
  erreur: "Impossible de charger ces données.",
  aideEnsemble: "↑ ↓ : se déplacer · → ← : ouvrir / fermer · Entrée : afficher · Alt+R : répartir · Alt+T : transférer · F1 : raccourcis",
  pagination: (debut, fin, total) => `${fmtInt(debut)}–${fmtInt(fin)} sur ${fmtInt(total)}`,
  page: (page, pages) => `Page ${fmtInt(page)} / ${fmtInt(pages)}`,
  pagePrecedente: "Précédent",
  pageSuivante: "Suivant",
  nonRenseigne: "—",
});

export const ARBRE = Object.freeze({
  label: "Arborescence des magasins",
  racine: "Stock global",
  nonRange: "Non rangé",
  chargement: "Chargement des emplacements…",
  erreur: "Emplacements indisponibles.",
  reessayer: "Réessayer",
  tronque: (n) => `… et ${fmtInt(n)} autres emplacements (onglet « Emplacements »)`,
  unites: (n) => unites(n),
  principal: "Principal",
  ferme: "Fermé",
  masque: "Masqué en caisse",
  nomNoeud: ({ libelle, unites: u }) => `${libelle}, ${unites(u)}`,
});

export const DETAIL = Object.freeze({
  racine: Object.freeze({
    titre: "Stock global",
    sousTitre: "Toutes les unités de l'officine, magasin par magasin.",
    tuiles: Object.freeze({
      unites: "Unités en stock",
      lots: "Lots",
      produits: "Produits",
      valeur: "Valeur du stock (PA HT)",
      ruptures: "Seuils franchis",
    }),
    grille: "Magasins",
    vide: "Aucun magasin.",
  }),
  magasin: Object.freeze({
    tuiles: Object.freeze({
      unites: "Unités",
      lots: "Lots",
      produits: "Produits",
      nonRange: "Non rangées",
      valeur: "Valeur (PA HT)",
      ruptures: "Seuils franchis",
    }),
    onglets: Object.freeze({ emplacements: "Emplacements", lots: "Lots du magasin", seuils: "Seuils franchis" }),
    ongletsLabel: "Contenu du magasin",
    grilleEmplacements: "Emplacements du magasin",
    videEmplacements: "Ce magasin n'a pas encore d'emplacement : ses unités sont « Non rangé ».",
    nonRangeLigne: "Non rangé",
    nonRangeAide: "Unités du magasin qui ne sont sur aucun emplacement",
    tronque: (n) => `Seuls les ${fmtInt(n)} premiers emplacements sont listés.`,
    grilleSeuils: "Seuils franchis du magasin",
    videSeuils: "Aucun seuil franchi dans ce magasin.",
    caisseVisible: "Visible en caisse",
    caisseMasque: "Masqué en caisse",
  }),
  emplacement: Object.freeze({
    sousTitre: ({ magasin, zone }) => [magasin, zone ? `zone ${zone}` : null].filter(Boolean).join(" · "),
    rangementHabituel: (nom) => `Rangement habituel : ${nom}`,
    tuiles: Object.freeze({ unites: "Unités rangées", lots: "Lots" }),
  }),
  nonRange: Object.freeze({
    titre: "Non rangé",
    sousTitre: (magasin) => `${magasin} — unités qui ne sont sur aucun emplacement`,
    tuiles: Object.freeze({ unites: "Unités non rangées", lots: "Lots" }),
  }),
  lots: Object.freeze({
    grille: "Lots",
    recherche: "Rechercher : produit, DCI, N° lot, code lot…",
    rechercheLabel: "Rechercher un lot de ce nœud",
    vide: (recherche) => (recherche ? `Aucun lot ne correspond à « ${recherche} » ici.` : "Aucun lot ici."),
    perime: "Périmé",
    /** WP-100: the lot a context menu acts on (its heading). */
    menuLot: (produit, numero) => (numero ? `${produit} · lot ${numero}` : `${produit} · lot non renseigné`),
    aide: "F2 : rechercher · Entrée : ouvrir le produit · Alt+R : répartir · Alt+T : transférer · Alt+C : corriger · clic droit ou Maj+F10 : actions",
  }),
  colonnes: Object.freeze({
    magasin: "Magasin",
    type: "Type",
    unites: "Unités",
    lots: "Lots",
    produits: "Produits",
    valeur: "Valeur PA HT",
    ruptures: "Seuils franchis",
    caisse: "Caisse",
    emplacement: "Emplacement",
    zone: "Zone",
    rangement: "Rangement habituel",
    produit: "Produit",
    numeroLot: "N° lot",
    peremption: "Péremption",
    ici: Object.freeze({ tous: "Dans le magasin", emplacement: "Sur l'emplacement", non_range: "Non rangé" }),
    alloue: "Dans le magasin",
    nonRange: "dont non rangé",
    total: "Lot (total)",
    vendable: "Vendable",
    bloqueReserve: "Bloqué · réservé",
    stock: "Stock",
    seuil: "Seuil",
    manque: "Manque",
  }),
});

export const VUE_EMPLACEMENTS = Object.freeze({
  grille: "Emplacements de l'officine",
  recherche: "Rechercher : code, zone, produit…",
  rechercheLabel: "Rechercher un emplacement",
  magasin: "Magasin",
  tousMagasins: "Tous les magasins",
  vide: (filtre) => (filtre ? "Aucun emplacement ne correspond aux filtres." : "Aucun emplacement. Créez le premier avec « Nouvel emplacement »."),
  colonnes: Object.freeze({
    code: "Code",
    zone: "Zone",
    magasin: "Magasin",
    rangement: "Rangement habituel",
    unites: "Unités rangées",
    lots: "Lots",
  }),
  aide: "F2 : rechercher · F3 : nouvel emplacement · Entrée : ouvrir · Alt+O : modifier · Suppr : supprimer",
});

export const VUE_TRANSFERTS = Object.freeze({
  grille: "Transferts entre magasins",
  statuts: "État",
  tous: "Tous",
  magasin: "Magasin",
  tousMagasins: "Tous les magasins",
  recherche: "Rechercher un N° de transfert…",
  rechercheLabel: "Rechercher un transfert",
  vide: (filtre) => (filtre ? "Aucun transfert ne correspond aux filtres." : "Aucun transfert. Créez-en un avec « Nouveau transfert »."),
  colonnes: Object.freeze({
    numero: "N°",
    date: "Date",
    source: "De",
    destination: "Vers",
    lignes: "Lignes",
    unites: "Unités",
    statut: "État",
    motif: "Motif",
  }),
  aide: "F2 : rechercher · F3 : nouveau transfert · Entrée : ouvrir · Maj+F10 : actions",
  tiroir: Object.freeze({
    titre: (numero) => `Transfert ${numero}`,
    sousTitre: ({ source, destination, date }) => `${source} → ${destination} · ${date}`,
    lignes: "Lignes du transfert",
    produit: "Produit",
    lot: "N° lot",
    peremption: "Péremption",
    quantite: "Quantité",
    fefo: "Un lot plus ancien du même produit est déjà dans le magasin de destination",
    motif: "Motif",
    valideLe: (date) => `Validé le ${date}`,
    fermer: "Fermer",
  }),
  confirmerValidation: Object.freeze({
    titre: "Valider ce transfert ?",
    message: "Les unités changent de magasin maintenant ; les quantités des lots ne changent pas.",
    lignes: "Lignes",
    unites: "Unités",
    trajet: "Trajet",
    bouton: "Valider le transfert",
  }),
  confirmerAnnulation: Object.freeze({
    titre: "Annuler ce brouillon ?",
    message: "Le brouillon est conservé avec l'état « Annulé » ; aucune unité ne bouge.",
    bouton: "Annuler le brouillon",
  }),
  valide: (numero, n) => `Transfert ${numero} validé : ${unites(n)} déplacées.`,
  annule: (numero) => `Brouillon ${numero} annulé.`,
});

export const REPARTIR = Object.freeze({
  titre: "Répartir le lot",
  sousTitre: ({ produit, numero, total }) =>
    [produit, numero ? `Lot ${numero}` : null, `${unites(total)} au total`].filter(Boolean).join(" · "),
  intro: "Indiquez combien d'unités se trouvent dans chaque magasin ; l'emplacement est facultatif.",
  magasin: "Magasin",
  quantite: "Unités dans le magasin",
  quantiteLabel: (nom) => `Unités dans « ${nom} »`,
  emplacements: "Emplacements",
  emplacement: "Emplacement",
  emplacementLabel: (nom) => `Emplacement dans « ${nom} »`,
  emplacementQuantiteLabel: (code) => `Unités sur ${code}`,
  emplacementChoisir: "Choisir un emplacement…",
  emplacementVide: "Aucun emplacement dans ce magasin",
  ajouterEmplacement: "Ajouter un emplacement",
  retirerEmplacement: (code) => `Retirer ${code ?? "cet emplacement"}`,
  nonRange: (n) => `Non rangé : ${fmtInt(n)}`,
  ferme: "Fermé : ne peut pas recevoir d'unités",
  principal: "Principal",
  reste: (n) => (n === 0 ? "Reste à placer : 0" : n > 0 ? `Reste à placer : ${fmtInt(n)}` : `${fmtInt(-n)} de trop`),
  // the server's MESSAGES_PLACEMENT, verbatim (parity-tested)
  somme: (total) => `La somme des magasins doit égaler le stock du lot (${total})`,
  emplacementsDepassent: (magasin, alloue) => `Les emplacements de « ${magasin} » dépassent les unités du lot dans ce magasin (${alloue})`,
  magasinFerme: (magasin) => `Le magasin « ${magasin} » est fermé : il ne peut pas recevoir d'unités`,
  emplacementEnDouble: (code) => `L'emplacement « ${code} » figure deux fois`,
  emplacementRequis: "Choisissez l'emplacement ou retirez la ligne",
  quantiteInvalide: "Nombre entier positif ou nul",
  pied: "F9 : enregistrer · Échap : annuler",
  enregistrer: "Enregistrer la répartition",
  annuler: "Annuler",
  succes: (numero) => (numero ? `Répartition du lot ${numero} enregistrée.` : "Répartition enregistrée."),
  erreurChargement: "Impossible de lire la répartition de ce lot.",
  reessayer: "Réessayer",
  aucunChangement: "La répartition est inchangée.",
});

export const CHOISIR_LOT = Object.freeze({
  titrePlacer: (magasin) => `Placer un lot dans « ${magasin} »`,
  titreRanger: (code) => `Ranger un lot sur ${code}`,
  introPlacer: "Choisissez le lot : sa répartition s'ouvre sur ce magasin.",
  introRanger: "Choisissez un lot de ce magasin : sa répartition s'ouvre avec cet emplacement.",
  lot: "Lot",
  placeholder: "Produit, N° lot, code lot…",
  vide: "Aucun lot ne correspond.",
  meta: ({ numero, peremption, unites: n }) => [numero ? `Lot ${numero}` : null, peremption, n != null ? unites(n) : null].filter(Boolean).join(" · "),
  annuler: "Annuler",
});

export const TRANSFERER = Object.freeze({
  titre: "Nouveau transfert entre magasins",
  source: "De (magasin source)",
  destination: "Vers (magasin de destination)",
  choisir: "— Choisir —",
  lots: "Lots à transférer",
  lotPicker: "Ajouter un lot du magasin source",
  lotPickerPlaceholder: "Produit, N° lot…",
  lotPickerVide: "Aucun lot de ce magasin ne correspond.",
  lotPickerAvant: "Choisissez d'abord le magasin source.",
  lotMeta: ({ numero, peremption, detenu }) => [numero ? `Lot ${numero}` : null, peremption, `${unites(detenu)} dans le magasin`].filter(Boolean).join(" · "),
  quantite: "Quantité",
  quantiteLabel: (nom) => `Quantité de ${nom}`,
  maximum: (n) => `Maximum : ${fmtInt(n)}`,
  retirer: (nom) => `Retirer ${nom}`,
  motif: "Motif (facultatif)",
  motifPlaceholder: "ex. Réassort du comptoir",
  resume: (lignes, n) => `${fmtInt(lignes)} ${pluriel(lignes, "ligne", "lignes")} · ${unites(n)}`,
  aucuneLigne: "Ajoutez au moins un lot.",
  memeMagasin: "Le magasin source et la destination doivent différer.",
  sourceRequise: "Choisissez le magasin source.",
  destinationRequise: "Choisissez le magasin de destination.",
  quantiteMax: (n) => `Entre 1 et ${fmtInt(n)}`,
  enregistrer: "Enregistrer le brouillon",
  transferer: "Transférer maintenant",
  annuler: "Annuler",
  pied: "F9 : transférer maintenant · Ctrl+S : brouillon · Échap : annuler",
  brouillonCree: (numero) => `Brouillon ${numero} enregistré — à valider dans l'onglet « Transferts ».`,
  transfere: (numero, n, source, destination) => `Transfert ${numero} : ${unites(n)} de ${source} vers ${destination}.`,
  brouillonRestant: (numero) => `Le brouillon ${numero} est enregistré : corrigez-le puis validez-le dans l'onglet « Transferts ».`,
  fermeExclu: "Les magasins fermés ne peuvent pas recevoir d'unités.",
});

export const AJUSTER = Object.freeze({
  titre: "Ajuster la répartition",
  sousTitre: ({ produit, numero, magasin }) => [produit, numero ? `Lot ${numero}` : null, magasin].filter(Boolean).join(" · "),
  intro: (reservoir) =>
    `Comptez les unités de ce lot dans ce magasin : la différence est reprise par « ${reservoir} », le magasin d'origine du lot. La quantité du lot ne change pas.`,
  actuel: "Unités dans ce magasin (système)",
  compte: "Unités comptées dans ce magasin",
  maximum: (n) => `Maximum : ${fmtInt(n)} (le reste du lot est dans les autres magasins)`,
  ecart: "Écart",
  ecartValeur: (n) => signe(n),
  motif: "Motif",
  motifPlaceholder: "ex. Comptage de la réserve",
  motifRequis: "Indiquez le motif",
  compteInvalide: (max) => `Entre 0 et ${fmtInt(max)}`,
  aucunEcart: "Aucun écart avec le système.",
  reservoir: (magasin) =>
    `« ${magasin} » est le magasin d'origine du lot : il reçoit la différence des autres magasins. Ajustez un autre magasin ou utilisez « Répartir ».`,
  ouvrirRepartir: "Répartir le lot",
  valider: "Enregistrer l'ajustement",
  annuler: "Annuler",
  pied: "F9 : enregistrer · Échap : annuler",
  succes: (magasin, avant, apres) => `« ${magasin} » : ${fmtInt(avant)} → ${fmtInt(apres)}.`,
  erreurChargement: "Impossible de lire la répartition de ce lot.",
});

export const HISTORIQUE = Object.freeze({
  titre: "Historique de répartition",
  sousTitre: ({ numero, magasin }) => [numero ? `Lot ${numero}` : null, magasin].filter(Boolean).join(" · "),
  grille: "Ajustements de la répartition",
  aide: "Les corrections de quantité (mot de passe) sont dans « Corrections de stock ».",
  colonnes: Object.freeze({ date: "Date", avant: "Avant", apres: "Après", ecart: "Écart", motif: "Motif", par: "Par" }),
  vide: "Aucun ajustement de répartition pour ce lot dans ce magasin.",
  fermer: "Fermer",
  ecart: (n) => signe(n),
});

export const FORM_MAGASIN = Object.freeze({
  titreNouveau: "Nouveau magasin",
  titreModifier: (nom) => `Modifier « ${nom} »`,
  code: "Code",
  codeAide: "Court et unique, ex. RES ou 002",
  nom: "Nom",
  nomPlaceholder: "ex. Réserve centrale",
  type: "Type",
  wilaya: "Wilaya",
  wilayaAucune: "— Aucune —",
  adresse: "Adresse",
  statut: "État",
  statutAide: "Un magasin fermé garde ses unités mais n'en reçoit plus.",
  principalProtege: "Le magasin principal reçoit les achats : il reste actif et garde son type.",
  visibleCaissier: "Visible aux caissiers en vente",
  // X XWP-95 / XWP-97 (XO-23 (6)) — a hidden magasin hides ITS LOTS from the caissiers (sale and search)
  visibleAide: "Décoché : les lots rangés dans ce magasin ne sont ni proposés ni visibles aux caissiers, à la caisse comme dans la recherche.",
  // X XWP-97 (XO-23 (7)) — the server's MESSAGES_LOTS_COMPTOIR.COMPTOIR_TOUJOURS_VISIBLE, said before saving
  comptoirToujoursVisible: "Un magasin Comptoir est toujours visible aux caissiers.",
  requis: "Obligatoire",
  trop: (n) => `${fmtInt(n)} caractères au plus`,
  enregistrer: "Enregistrer",
  annuler: "Annuler",
  cree: (nom) => `Magasin « ${nom} » créé.`,
  modifie: (nom) => `Magasin « ${nom} » mis à jour.`,
});

export const FORM_EMPLACEMENT = Object.freeze({
  titreNouveau: "Nouvel emplacement",
  titreModifier: (code) => `Modifier l'emplacement ${code}`,
  code: "Code",
  codeAide: "Format « AA-00 » (ex. : AA-01, B12)",
  codeFormat: "Format attendu « AA-00 » (ex. : AA-01, B12)",
  magasin: "Magasin",
  zone: "Zone (facultatif)",
  zonePlaceholder: "ex. A",
  rangement: "Rangement habituel (facultatif)",
  rangementAide: "Le produit qu'on y range d'habitude ; d'autres produits peuvent y être rangés.",
  rangementPlaceholder: "Rechercher un produit…",
  notes: "Notes (facultatif)",
  occupe: "Cet emplacement contient des unités : videz-le avant de le rattacher à un autre magasin.",
  requis: "Obligatoire",
  trop: (n) => `${fmtInt(n)} caractères au plus`,
  enregistrer: "Enregistrer",
  annuler: "Annuler",
  cree: (code) => `Emplacement ${code} créé.`,
  modifie: (code) => `Emplacement ${code} mis à jour.`,
  supprimerTitre: (code) => `Supprimer l'emplacement ${code} ?`,
  supprimerMessage: (n) =>
    n > 0
      ? `Ses ${unites(n)} rangées repassent en « Non rangé » dans leur magasin ; aucune quantité de stock ne change.`
      : "Il ne contient aucune unité.",
  supprimerBouton: "Supprimer",
  supprime: (code) => `Emplacement ${code} supprimé.`,
});

/** Labels of the shortcut sheet groups the screen maps use. */
export const GROUPES_RACCOURCIS = Object.freeze({ lots: "Lots", document: "Document" });
